import React from 'react'
import { Link } from 'react-router-dom'
import { 
  Trophy, 
  Users, 
  Target, 
  ArrowRight, 
  Zap,
  Calendar,
  TrendingUp,
  BarChart3,
  CheckCircle,
  Globe,
  Flame,
  Crown,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { useAuthStore } from '@/stores/authStore'
import { usePremiumStore } from '@/stores/premiumStore'

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuthStore()
  const { isPremium } = usePremiumStore()

  return (
    <main className="min-h-screen bg-dark-700">
      {/* Hero Section — World Cup Featured */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse-fire" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl animate-pulse-fire delay-700" />
        </div>

        <div className="relative container-app">
          <div className="text-center max-w-4xl mx-auto">
            {/* Featured Badge */}
            <div className="mb-6">
              <Badge variant="outline" size="lg" className="px-4 py-2 bg-emerald-950 border-emerald-400 text-emerald-200">
                ⚽ NEW — World Cup Blitz 2026 is Live!
              </Badge>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-heading font-bold mb-6 leading-tight">
              <span className="text-gradient-fire">Predict. Compete.</span>
              <br />
              <span className="text-white">Win Every Bracket.</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-text-secondary mb-10 max-w-3xl mx-auto leading-relaxed">
              The bracket prediction platform for every major tournament. 
              Powered by data, built for competition.
            </p>

            {/* Sport Cards — WC Featured */}
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
              {/* World Cup Card — Featured */}
              <Link to="/wc/knockout" className="group">
                <Card className="h-full border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-green-700/10 hover:border-emerald-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10">
                  <CardBody className="p-6 text-left">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Globe className="h-6 w-6 text-emerald-400" />
                        <Badge className="bg-emerald-500/20 border-emerald-500/40 text-emerald-300 text-[10px]">
                          LIVE NOW
                        </Badge>
                      </div>
                      <span className="text-2xl">⚽</span>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">World Cup Blitz 2026</h2>
                    <p className="text-sm text-text-secondary mb-4">
                      32-team knockout bracket. Pick every winner, crown a champion, and compete with friends.
                    </p>
                    <div className="flex items-center text-emerald-400 text-sm font-semibold group-hover:gap-2 transition-all">
                      Play Knockout <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardBody>
                </Card>
              </Link>

              {/* March Madness Card */}
              <Link to="/wall" className="group">
                <Card className="h-full border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-red-700/10 hover:border-orange-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/10">
                  <CardBody className="p-6 text-left">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-6 w-6 text-orange-400" />
                        <Badge className="bg-orange-500/20 border-orange-500/40 text-orange-300 text-[10px]">
                          SWEET 16
                        </Badge>
                      </div>
                      <span className="text-2xl">🏀</span>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">March Madness 2026</h2>
                    <p className="text-sm text-text-secondary mb-4">
                      Full NCAA tournament bracket with MarchMetrix analytics, upset radar, and live scores.
                    </p>
                    <div className="flex items-center text-orange-400 text-sm font-semibold group-hover:gap-2 transition-all">
                      View Bracket <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardBody>
                </Card>
              </Link>
            </div>

            {/* Auth CTA */}
            {!isAuthenticated && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Link to="/register">
                  <Button size="lg" className="text-lg px-8 py-4 shadow-glow">
                    <span className="flex items-center">
                      Create Free Account
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                    Sign In
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* World Cup launch state */}
          <div className="max-w-2xl mx-auto">
            <Card className="glass border-emerald-500/20">
              <CardBody className="text-center py-6">
                <div className="flex items-center justify-center mb-4">
                  <Calendar className="h-5 w-5 text-emerald-400 mr-2" />
                  <h3 className="text-base font-semibold text-text-primary">
                    ⚽ World Cup Knockout Is Open
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Teams', value: '32' },
                    { label: 'Picks', value: '31' },
                    { label: 'Champion', value: '1' },
                  ].map((unit) => (
                    <div key={unit.label} className="text-center">
                      <div className="text-3xl font-heading font-bold text-emerald-400 mb-1">
                        {unit.value}
                      </div>
                      <div className="text-xs text-slate-300 font-medium">{unit.label}</div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* World Cup Features Spotlight */}
      <section className="py-16 bg-dark-600 border-t border-dark-500">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              World Cup Blitz — What Makes It Different
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Not just another bracket app. Real analytics. Real competition.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Zap,
                title: 'Knockout Mode',
                description: 'Start at the Round of 32, pick each winner, and crown your champion in minutes.',
                color: 'text-yellow-400',
                bg: 'from-yellow-500/10 to-yellow-600/5',
              },
              {
                icon: BarChart3,
                title: 'Elo-Powered Odds',
                description: 'Every matchup shows win probability based on our proprietary Elo model. Not vibes — math.',
                color: 'text-blue-400',
                bg: 'from-blue-500/10 to-blue-600/5',
              },
              {
                icon: TrendingUp,
                title: 'Real Form Data',
                description: 'Live recent results from API-Football. See who\'s hot and who\'s struggling before you pick.',
                color: 'text-emerald-400',
                bg: 'from-emerald-500/10 to-emerald-600/5',
              },
              {
                icon: Target,
                title: 'Path Builder',
                description: 'Trace the road to the title and pressure-test the matchups before you lock your bracket.',
                color: 'text-purple-400',
                bg: 'from-purple-500/10 to-purple-600/5',
              },
            ].map((feat) => {
              const Icon = feat.icon
              return (
                <Card key={feat.title} className={`bg-gradient-to-br ${feat.bg} border-gray-800/50 hover:border-gray-700 transition-all`}>
                  <CardBody className="p-6">
                    <Icon className={`h-8 w-8 ${feat.color} mb-4`} />
                    <h3 className="text-lg font-bold text-white mb-2">{feat.title}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{feat.description}</p>
                  </CardBody>
                </Card>
              )
            })}
          </div>

          <div className="text-center mt-8">
            <Link to="/wc/knockout">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8">
                <Globe className="mr-2 h-5 w-5" />
                Play World Cup Knockout →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t border-dark-500">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              How It Works
            </h2>
            <p className="text-lg text-text-secondary">
              Three steps. Any sport. One winner.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: 1,
                title: 'Pick',
                description: 'Fill the knockout bracket with data-powered insights on every matchup.',
                icon: Target,
              },
              {
                step: 2,
                title: 'Compete',
                description: 'Share your bracket, challenge friends, and climb the leaderboard.',
                icon: Users,
              },
              {
                step: 3,
                title: 'Win',
                description: 'Track live scores, earn points for correct picks, and claim bragging rights.',
                icon: Trophy,
              },
            ].map((step, index) => {
              const Icon = step.icon
              return (
                <div key={step.step} className="text-center relative">
                  {index < 2 && (
                    <div className="hidden md:block absolute top-12 left-3/4 w-full h-px bg-gradient-to-r from-primary-500/50 to-transparent z-0" />
                  )}
                  <div className="relative z-10">
                    <div className="h-20 w-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg">
                      <div className="text-center">
                        <div className="text-white font-bold text-xs mb-0.5">{step.step}</div>
                        <Icon className="h-6 w-6 text-white mx-auto" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">{step.title}</h3>
                    <p className="text-text-secondary leading-relaxed">{step.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* March Madness Section */}
      <section className="py-16 bg-dark-600 border-t border-dark-500">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-lg">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="h-6 w-6 text-orange-400" />
                <Badge className="bg-orange-500/20 border-orange-500/40 text-orange-300">NCAA Tournament</Badge>
              </div>
              <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
                March Madness Analytics
              </h2>
              <p className="text-text-secondary mb-6 leading-relaxed">
                Full interactive bracket with MarchMetrix MMER predictions, upset radar, edge finder vs Vegas lines, 
                and deep team analytics. The same engine. Different sport.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/wall">
                  <Button className="bg-orange-500 hover:bg-orange-400 text-black font-bold">
                    View NCAA Bracket →
                  </Button>
                </Link>
                <Link to="/edges">
                  <Button variant="outline">MMER vs Vegas</Button>
                </Link>
                <Link to="/tv">
                  <Button variant="outline">TV Command Center</Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'MMER Predictions', icon: BarChart3, color: 'text-blue-400' },
                { label: 'Live Scores', icon: Zap, color: 'text-yellow-400' },
                { label: 'Upset Radar', icon: TrendingUp, color: 'text-red-400' },
                { label: 'Edge Finder', icon: Target, color: 'text-emerald-400' },
              ].map((item) => {
                const Icon = item.icon
                return (
                  <Card key={item.label} className="border-gray-800/50">
                    <CardBody className="p-4 text-center">
                      <Icon className={`h-6 w-6 ${item.color} mx-auto mb-2`} />
                      <span className="text-xs font-semibold text-text-secondary">{item.label}</span>
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 border-t border-dark-500">
        <div className="container-app text-center">
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-12">
            Built Different
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {[
              { label: 'Sports', value: '2', sub: 'NCAA + World Cup' },
              { label: 'Data Points', value: '10K+', sub: 'Elo + Form + API' },
              { label: 'Matchups Analyzed', value: '500+', sub: 'With win probability' },
              { label: 'Test Coverage', value: '161', sub: 'Tests passing' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-heading font-bold text-gradient-primary mb-1">{stat.value}</div>
                <div className="text-sm font-semibold text-text-primary mb-0.5">{stat.label}</div>
                <div className="text-[10px] text-text-muted">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium CTA */}
      {!isPremium && (
        <section className="py-16 bg-gradient-to-r from-yellow-400/10 to-yellow-500/10 border-t border-yellow-500/20">
          <div className="container-app text-center max-w-3xl mx-auto">
            <Crown className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gradient-premium mb-4">
              BracketBlitz Premium
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Unlimited brackets, advanced analytics, early alerts, and premium features across all sports.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {['Unlimited Brackets', 'Advanced Analytics', 'Early Alerts', 'All Sports Access'].map((b) => (
                <div key={b} className="flex items-center justify-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">{b}</span>
                </div>
              ))}
            </div>
            <Link to="/premium">
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 font-bold px-8 shadow-premium hover:from-yellow-300 hover:to-yellow-400">
                <Crown className="mr-2 h-5 w-5" />
                Upgrade — $4.99/season
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-500/10 to-primary-500/10 border-t border-emerald-500/20">
        <div className="container-app text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Ready to Play?
          </h2>
          <p className="text-lg text-text-secondary mb-8 max-w-xl mx-auto">
            World Cup knockout brackets are open. Pick every winner and crown your champion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/wc/knockout">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 text-lg">
                ⚽ World Cup Blitz
              </Button>
            </Link>
            {!isAuthenticated && (
              <Link to="/register">
                <Button variant="outline" size="lg" className="text-lg px-8">
                  Create Account
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-800 border-t border-dark-600 py-12">
        <div className="container-app">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <Trophy className="h-7 w-7 text-primary-400" />
                <span className="text-xl font-heading font-bold text-text-primary">BracketBlitz</span>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                The multi-sport bracket prediction platform. NCAA Basketball. FIFA World Cup. More coming soon.
              </p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">Powered by MMER</Badge>
                <Badge variant="outline" className="text-[10px]">API-Football</Badge>
              </div>
            </div>

            <div>
              <h2 className="font-semibold text-text-primary mb-3 text-sm">World Cup 2026</h2>
              <ul className="space-y-1.5 text-sm">
                <li><Link to="/wc/knockout" className="text-text-secondary hover:text-emerald-400 transition-colors">Knockout Bracket</Link></li>
                <li><Link to="/wc/stats" className="text-text-secondary hover:text-emerald-400 transition-colors">Team Stats</Link></li>
                <li><Link to="/wc/h2h" className="text-text-secondary hover:text-emerald-400 transition-colors">Head-to-Head</Link></li>
                <li><Link to="/wc/path" className="text-text-secondary hover:text-emerald-400 transition-colors">Path to Glory</Link></li>
                <li><Link to="/wc/leaderboard" className="text-text-secondary hover:text-emerald-400 transition-colors">Leaderboard</Link></li>
              </ul>
            </div>

            <div>
              <h2 className="font-semibold text-text-primary mb-3 text-sm">March Madness</h2>
              <ul className="space-y-1.5 text-sm">
                <li><Link to="/wall" className="text-text-secondary hover:text-orange-400 transition-colors">Bracket Wall</Link></li>
                <li><Link to="/edges" className="text-text-secondary hover:text-orange-400 transition-colors">Edge Finder</Link></li>
                <li><Link to="/tv" className="text-text-secondary hover:text-orange-400 transition-colors">TV Mode</Link></li>
                <li><Link to="/compare" className="text-text-secondary hover:text-orange-400 transition-colors">Compare</Link></li>
              </ul>
            </div>

            <div>
              <h2 className="font-semibold text-text-primary mb-3 text-sm">Account & Legal</h2>
              <ul className="space-y-1.5 text-sm">
                <li><Link to="/register" className="text-text-secondary hover:text-primary-400 transition-colors">Sign Up Free</Link></li>
                <li><Link to="/login" className="text-text-secondary hover:text-primary-400 transition-colors">Sign In</Link></li>
                <li><Link to="/premium" className="text-text-secondary hover:text-yellow-400 transition-colors">Premium</Link></li>
                <li><Link to="/privacy" className="text-text-secondary hover:text-primary-400 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-text-secondary hover:text-primary-400 transition-colors">Terms</Link></li>
                <li><Link to="/official-rules" className="text-text-secondary hover:text-primary-400 transition-colors">Official Rules</Link></li>
                <li><Link to="/privacy-choices" className="text-text-secondary hover:text-primary-400 transition-colors">Privacy Choices</Link></li>
                <li><Link to="/accessibility" className="text-text-secondary hover:text-primary-400 transition-colors">Accessibility</Link></li>
                <li><Link to="/help" className="text-text-secondary hover:text-primary-400 transition-colors">Help</Link></li>
                <li><Link to="/contact" className="text-text-secondary hover:text-primary-400 transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-dark-600 mt-8 pt-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <p className="text-xs text-text-muted">© 2026 BracketBlitz. All rights reserved.</p>
              <p className="text-xs text-text-muted md:text-right">
                Independent fan games. No purchase necessary for free challenges. Void where
                prohibited.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </main>
  )
}

export { HomePage }
